import { LoadingButton } from "@mui/lab";
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import {
  isWorkstationInList,
  sortWorkstationsByGrid
} from "~/features/preview/workstation/settingsWorkstation.logic";
import {
  settingsInputMinHeightPx,
  settingsOverviewHelperFontSize,
  settingsOverviewLabelControlGap,
  settingsSelectSx,
  settingsSiteSelectMaxWidthPx
} from "~/features/settings/settings.styles";
import { selectSelectedGridId } from "~/features/workstationConfig/workstationConfig.slice";
import { useSetThisWorkstation } from "~/hooks/useSetThisWorkstation";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import { WorkstationSummaryDto } from "~/types/api";

export function WorkstationPanel() {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const userFc = useAppSelector(selectUsersFulfillmentCenter);
  const selectedGridId = useAppSelector(selectSelectedGridId);
  const { setThisWorkstation, isLoading: isSettingWorkstation } =
    useSetThisWorkstation();
  const [pendingWorkstation, setPendingWorkstation] =
    useState<WorkstationSummaryDto | null>(siteWorkstation);
  const { data, isLoading } = useGetWorkstationsQuery(undefined, {
    skip: !userFc
  });

  const sortedWorkstations = useMemo(() => {
    if (!data) {
      return undefined;
    }
    return sortWorkstationsByGrid(data, selectedGridId);
  }, [data, selectedGridId]);

  useEffect(() => {
    setPendingWorkstation(siteWorkstation);
  }, [siteWorkstation]);

  useEffect(() => {
    if (!sortedWorkstations) {
      return;
    }
    setPendingWorkstation((currentWs) => {
      if (!currentWs) {
        return currentWs;
      }
      if (isWorkstationInList(sortedWorkstations, currentWs.id)) {
        return currentWs;
      }
      return isWorkstationInList(sortedWorkstations, siteWorkstation?.id)
        ? siteWorkstation
        : null;
    });
  }, [sortedWorkstations, siteWorkstation]);

  const siteWorkstationId = siteWorkstation?.id ?? "";
  const pendingWorkstationId =
    pendingWorkstation &&
    isWorkstationInList(sortedWorkstations, pendingWorkstation.id)
      ? pendingWorkstation.id
      : "";
  const isSiteWorkstationHiddenByGridFilter =
    Boolean(siteWorkstationId) &&
    !isWorkstationInList(sortedWorkstations, siteWorkstationId);

  const handleWorkstationChanged = useCallback(
    (e: SelectChangeEvent) => {
      const workstation = sortedWorkstations?.find(
        (workstation) => workstation.id === e.target.value
      );
      setPendingWorkstation(workstation || null);
    },
    [sortedWorkstations]
  );

  // Filter hidden site WS can show an empty select
  //  Do not treat that as unassign intent
  const isDropdownEmptyWhileSiteWsHiddenByGrid =
    !pendingWorkstationId && isSiteWorkstationHiddenByGridFilter;
  const isWorkstationDirty =
    pendingWorkstationId !== siteWorkstationId &&
    !isDropdownEmptyWhileSiteWsHiddenByGrid;
  const assignDisabled = !isWorkstationDirty || isSettingWorkstation;

  const handleSetThisWorkstationClicked = useCallback(async () => {
    const workstationToAssign = pendingWorkstationId
      ? pendingWorkstation
      : null;
    try {
      await setThisWorkstation(workstationToAssign, true);
      if (workstationToAssign) {
        successToast(t("workstation set"), {
          description: t("workstation set description", {
            workstation: workstationToAssign.deviceId
          })
        });
      } else {
        successToast(t("workstation unset"), {
          description: t("workstation unset description")
        });
      }
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
    }
  }, [
    setThisWorkstation,
    pendingWorkstation,
    pendingWorkstationId,
    successToast,
    errorToast,
    t
  ]);

  const helperText = t("select workstation id matches port helper");

  if (data === undefined && isLoading) {
    return (
      <Stack id="general-ws-assignment-loading" spacing={1}>
        <Typography variant="body2" color="text.secondary">
          {t("loading")}…
        </Typography>
      </Stack>
    );
  }

  if (sortedWorkstations && sortedWorkstations.length === 0 && !isLoading) {
    return (
      <Stack id="general-ws-assignment-empty" spacing={1}>
        <Typography variant="body2" color="text.secondary">
          {t("no workstations found")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack id="general-ws-assignment-root" spacing={1} sx={{ width: "100%" }}>
      <Box
        id="general-ws-assignment-row"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flexWrap: { md: "nowrap" },
          alignItems: { md: "center" },
          width: "100%",
          minWidth: 0,
          columnGap: { xs: 0, md: settingsOverviewLabelControlGap },
          rowGap: { xs: 1.5, md: 0 }
        }}
      >
        <Box
          id="general-ws-control-cluster"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "center" },
            flex: { md: "1 1 auto" },
            minWidth: 0,
            width: { xs: "100%", md: "auto" },
            gap: { xs: 1.5, md: 2 }
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: {
                xs: "100%",
                md: settingsSiteSelectMaxWidthPx
              },
              minWidth: 0,
              flex: { md: "0 1 auto" }
            }}
          >
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                id="general-layout-assign-workstation-select"
                fullWidth
                variant="outlined"
                value={pendingWorkstationId}
                onChange={handleWorkstationChanged}
                displayEmpty
                aria-describedby="workstation-layout-select-helper"
                sx={settingsSelectSx}
              >
                {sortedWorkstations?.map((workstation) => (
                  <MenuItem key={workstation.deviceId} value={workstation.id}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      width="100%"
                      justifyContent="space-between"
                    >
                      {`${workstation.deviceId}`}
                      <Chip
                        size="small"
                        label={
                          workstation.status === "Active"
                            ? t("workstation status chip in use")
                            : workstation.status
                        }
                        color={
                          workstation.status === "Active"
                            ? "warning"
                            : undefined
                        }
                      />
                    </Stack>
                  </MenuItem>
                ))}
                <MenuItem value="">
                  <em>{t("no workstation selected")}</em>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-end", md: "flex-start" },
              alignItems: "center",
              flexShrink: 0,
              minWidth: 0
            }}
          >
            <LoadingButton
              variant="contained"
              color="secondary"
              loading={isSettingWorkstation}
              disabled={assignDisabled}
              onClick={handleSetThisWorkstationClicked}
              aria-label={t("assign workstation")}
              sx={{
                flexShrink: 0,
                minHeight: settingsInputMinHeightPx,
                boxSizing: "border-box",
                maxWidth: "none",
                width: "auto",
                ...(assignDisabled && {
                  bgcolor: "transparent",
                  color: "secondary.main",
                  border: "1px solid",
                  borderColor: "secondary.main",
                  boxShadow: "none"
                })
              }}
            >
              {t("assign")}
            </LoadingButton>
          </Box>
        </Box>
      </Box>

      <Typography
        id="workstation-layout-select-helper"
        component="p"
        variant="body2"
        sx={{
          color: "text.secondary",
          m: 0,
          display: "block",
          width: "100%",
          minWidth: 0,
          fontSize: settingsOverviewHelperFontSize,
          lineHeight: 1.4,
          pl: 0
        }}
      >
        {helperText}
      </Typography>
    </Stack>
  );
}
